import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth, functions } from '../firebase-client';
import { useAuth } from '../auth/AuthContext';

const requestEmailVerification = httpsCallable(functions, 'requestEmailVerification');

function resolveSafeNextPath(rawNext, fallback = '/dashboard') {
  if (typeof rawNext !== 'string' || !rawNext.startsWith('/') || rawNext.startsWith('//')) return fallback;
  return rawNext;
}

export default function VerifyEmailPage() {
  const { user, authLoading, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search || '');
  const nextPath = resolveSafeNextPath(params.get('next'));
  const [status, setStatus] = useState(params.get('sent') === '1' ? 'sent' : 'idle');
  const [message, setMessage] = useState('');

  const sendVerification = async () => {
    setStatus('sending');
    setMessage('');
    try {
      const result = await requestEmailVerification();
      if (result?.data?.alreadyVerified) {
        await auth.currentUser?.reload();
        await auth.currentUser?.getIdToken(true);
        navigate(nextPath, { replace: true });
        return;
      }
      setStatus('sent');
    } catch (error) {
      setStatus('error');
      setMessage(error?.code === 'functions/resource-exhausted'
        ? 'Please wait a minute before requesting another email.'
        : 'We could not send the verification email. Please try again.');
    }
  };

  const checkVerification = async () => {
    setStatus('checking');
    setMessage('');
    try {
      await auth.currentUser?.reload();
      if (auth.currentUser?.emailVerified) {
        await auth.currentUser.getIdToken(true);
        navigate(nextPath, { replace: true });
        return;
      }
      setStatus('idle');
      setMessage('Your email is not verified yet. Open the link in the email, then try again.');
    } catch (error) {
      setStatus('error');
      setMessage('We could not check your account. Please try again.');
    }
  };

  if (authLoading) {
    return <div className="page"><section className="section"><p className="text-muted">Loading...</p></section></div>;
  }

  return (
    <div className="page">
      <section className="section">
        <div className="card" style={{ maxWidth: 520, marginInline: 'auto' }}>
          <h1 className="section-title">Verify your email</h1>
          {!user ? (
            <p>You need to <Link to={`/login?next=${encodeURIComponent(nextPath)}`}>log in</Link> before requesting a verification email.</p>
          ) : !user.email || user.emailVerified ? (
            <>
              <p style={{ color: '#166534' }}>
                {user.email ? 'Your email address is verified.' : 'This account does not require email verification.'}
              </p>
              <button type="button" className="button" onClick={() => navigate(nextPath, { replace: true })}>Continue</button>
            </>
          ) : (
            <>
              <p>We need to confirm that you own <strong>{user.email}</strong> before you continue.</p>
              {status === 'sent' && (
                <p style={{ color: '#166534' }}>Verification email sent. Check your inbox and spam folder.</p>
              )}
              {message && <p style={{ color: status === 'error' ? '#991b1b' : undefined }}>{message}</p>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                <button type="button" className="button" onClick={sendVerification} disabled={status === 'sending' || status === 'checking'}>
                  {status === 'sending' ? 'Sending...' : status === 'sent' ? 'Resend email' : 'Send verification email'}
                </button>
                <button type="button" className="button button-secondary" onClick={checkVerification} disabled={status === 'sending' || status === 'checking'}>
                  {status === 'checking' ? 'Checking...' : 'I’ve verified my email'}
                </button>
              </div>
              <button type="button" className="button button-secondary" style={{ marginTop: '1rem' }} onClick={signOut}>Use a different account</button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export { resolveSafeNextPath };
